"use client";

import { useMemo, useState } from "react";
import {
  ErrorNotice,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { WIDGET_H2 } from "@/components/tool-kit/styles";
import {
  EXPRESSION_ERROR_STRINGS,
  expressionErrorMessage,
} from "@/components/tools/calculus/expression-errors";
import { OdeResults } from "@/components/tools/calculus/ode-results";
import { readConstant } from "@/components/tools/calculus/read-constant";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import { parseIntegerInput, parseNumberInput } from "@/lib/parse-number";
import type { Node } from "@/lib/tool-kit/calculus/ast";
import { variablesOf } from "@/lib/tool-kit/calculus/ast";
import { compile, compile2 } from "@/lib/tool-kit/calculus/evaluate";
import { MAX_ODE_STEPS, type OdeMethod } from "@/lib/tool-kit/calculus/ode";
import { parseExpression } from "@/lib/tool-kit/calculus/parse";

/** English strings. A translated content file overrides these through `ui`. */
export const EULERS_METHOD_CALCULATOR_STRINGS = {
  functionLabel: "Slope f(x, y) in dy/dx = f(x, y)",
  functionHint:
    "Use x for the independent variable and y for the unknown, with + − * / ^ and brackets: x + y, x*y, y*(1 − y), sin(x) − y. Functions: sin, cos, tan, exp, ln, sqrt and more.",
  x0Label: "Initial x₀",
  y0Label: "Initial y₀ = y(x₀)",
  endLabel: "Solve up to x =",
  modeLabel: "Choose the steps by",
  modes: { step: "Step size h", count: "Number of steps n" },
  stepLabel: "Step size h",
  countLabel: "Number of steps n",
  methodLabel: "Method",
  methods: {
    euler: "Euler's method",
    heun: "Improved Euler (Heun)",
    rk4: "Runge–Kutta 4",
  },
  exactLabel: "Exact solution y(x) (optional)",
  exactHint:
    "If you know the exact solution, enter it in x to see the true error; otherwise a very accurate RK4 reference is used.",
  results: "Results",
  approximationLabel: "Approximation of y({end})",
  exactResultLabel: "Exact y({end})",
  referenceResultLabel: "Reference y({end})",
  errorLabel: "Error",
  stepLabelResult: "Step size h",
  stepsLabel: "Steps n",
  adjusted:
    "h = {requested} does not divide the interval evenly, so {n} equal steps of h = {h} were used instead.",
  formulaHeading: "The method",
  formulas: {
    euler:
      "yₙ₊₁ = yₙ + h·f(xₙ, yₙ): follow the slope at the start of each step. Global error is proportional to h.",
    heun: "k₁ = f(xₙ, yₙ), k₂ = f(xₙ + h, yₙ + h·k₁), yₙ₊₁ = yₙ + (h/2)(k₁ + k₂): average the slopes at both ends. Global error is proportional to h².",
    rk4: "yₙ₊₁ = yₙ + (h/6)(k₁ + 2k₂ + 2k₃ + k₄), with four slope samples per step. Global error is proportional to h⁴.",
  },
  tableHeading: "Step table",
  columnN: "n",
  columnX: "xₙ",
  columnY: "yₙ",
  columnSlope: "k{i}",
  columnExact: "Exact y(xₙ)",
  columnReference: "Reference",
  columnError: "Error",
  tableNote: "Showing the first and last rows of {count}.",
  skipped: "…",
  blowUp:
    "The solution became infinite near x = {at}, so the table stops there. Some equations blow up in finite time (y′ = y² from y(0) = 1 at x = 1); a smaller step cannot cure that.",
  compareHeading: "The three methods with the same step",
  columnMethod: "Method",
  columnEnd: "y at the end",
  studyHeading: "Halving the step ({method})",
  columnStepSize: "h",
  studyNote:
    "For Euler's method the error roughly halves when h is halved; for Heun's it falls to a quarter; for RK4 to a sixteenth.",
  graphHeading: "Graph",
  graphTitle:
    "Slope field of dy/dx = f(x, y) with the {method} solution from x = {x0} to x = {end}",
  seriesNumeric: "{method}",
  seriesExact: "Exact solution",
  seriesReference: "Reference (RK4)",
  downloadPng: "Download graph (PNG)",
  downloadSvg: "Download graph (SVG)",
  noExactEnd: "—",
  onlyXY:
    "Use only x and y in the slope: x is the independent variable and y the unknown.",
  exactOnlyX: "The exact solution should use only x.",
  valueInvalid: "Enter {field} as a number or constant such as 0, 1.5 or pi/2.",
  stepInvalid: "Enter a nonzero step size h.",
  countInvalid: "Enter n as a whole number from 1 to {max}.",
  sameEnds: "The end x must differ from x₀.",
  expressionErrors: EXPRESSION_ERROR_STRINGS,
} as const;

export type OdeStrings = typeof EULERS_METHOD_CALCULATOR_STRINGS;

export type OdeView =
  | { kind: "problem"; message: string }
  | {
      kind: "ok";
      ast: Node;
      f: (x: number, y: number) => number;
      exact: ((x: number) => number) | null;
      x0: number;
      y0: number;
      xEnd: number;
      n: number;
      h: number;
      requestedH: number | null;
      method: OdeMethod;
    };

type Mode = "step" | "count";
const METHODS: readonly OdeMethod[] = ["euler", "heun", "rk4"];
const MODES: readonly Mode[] = ["step", "count"];

interface Inputs {
  text: string;
  x0Text: string;
  y0Text: string;
  endText: string;
  mode: Mode;
  amountText: string;
  exactText: string;
  method: OdeMethod;
}

function readValues(
  inputs: Inputs,
  strings: OdeStrings
): { values: Record<string, number> } | { error: string } {
  const fields = [
    ["x0", inputs.x0Text, strings.x0Label],
    ["y0", inputs.y0Text, strings.y0Label],
    ["end", inputs.endText, strings.endLabel],
  ] as const;
  const values: Record<string, number> = {};
  for (const [key, text, label] of fields) {
    const read = readConstant(text);
    if (read === null || read === "invalid") {
      return { error: t(strings.valueInvalid, { field: label }) };
    }
    values[key] = read.value;
  }
  return { values };
}

/** n and h from either the step size or the count; null with a message if invalid. */
function readSteps(
  inputs: Inputs,
  span: number,
  strings: OdeStrings
): { n: number; h: number; requestedH: number | null } | { error: string } {
  if (inputs.mode === "count") {
    const n = parseIntegerInput(inputs.amountText);
    if (n === null || n < 1 || n > MAX_ODE_STEPS) {
      return { error: t(strings.countInvalid, { max: MAX_ODE_STEPS }) };
    }
    return { n, h: span / n, requestedH: null };
  }
  const requested = parseNumberInput(inputs.amountText);
  if (requested === null || requested === 0) {
    return { error: strings.stepInvalid };
  }
  const n = Math.ceil(Math.abs(span / requested) - 1e-9);
  if (n < 1 || n > MAX_ODE_STEPS) {
    return { error: t(strings.countInvalid, { max: MAX_ODE_STEPS }) };
  }
  const exact = Math.abs(Math.abs(span / requested) - n) < 1e-9;
  return { n, h: span / n, requestedH: exact ? null : Math.abs(requested) };
}

function buildView(inputs: Inputs, strings: OdeStrings): OdeView {
  const parsed = parseExpression(inputs.text);
  if (!parsed.ok) {
    return {
      kind: "problem",
      message: expressionErrorMessage(parsed.error, strings.expressionErrors),
    };
  }
  if (parsed.variables.some((name) => name !== "x" && name !== "y")) {
    return { kind: "problem", message: strings.onlyXY };
  }
  const read = readValues(inputs, strings);
  if ("error" in read) {
    return { kind: "problem", message: read.error };
  }
  const x0 = read.values.x0 ?? Number.NaN;
  const y0 = read.values.y0 ?? Number.NaN;
  const end = read.values.end ?? Number.NaN;
  if (end === x0) {
    return { kind: "problem", message: strings.sameEnds };
  }
  const steps = readSteps(inputs, end - x0, strings);
  if ("error" in steps) {
    return { kind: "problem", message: steps.error };
  }
  let exact: ((x: number) => number) | null = null;
  if (inputs.exactText.trim() !== "") {
    const solution = parseExpression(inputs.exactText);
    if (!solution.ok) {
      return {
        kind: "problem",
        message: expressionErrorMessage(
          solution.error,
          strings.expressionErrors
        ),
      };
    }
    if (variablesOf(solution.ast).some((name) => name !== "x")) {
      return { kind: "problem", message: strings.exactOnlyX };
    }
    exact = compile(solution.ast, "x");
  }
  return {
    kind: "ok",
    ast: parsed.ast,
    f: compile2(parsed.ast, "x", "y"),
    exact,
    x0,
    y0,
    xEnd: end,
    n: steps.n,
    h: steps.h,
    requestedH: steps.requestedH,
    method: inputs.method,
  };
}

export default function EulersMethodCalculatorWidget() {
  const strings = useToolUi(EULERS_METHOD_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("x + y");
  const [x0Text, setX0Text] = useState("0");
  const [y0Text, setY0Text] = useState("1");
  const [endText, setEndText] = useState("1");
  const [mode, setMode] = useState<Mode>("step");
  const [amountText, setAmountText] = useState("0.1");
  const [method, setMethod] = useState<OdeMethod>("euler");
  const [exactText, setExactText] = useState("2e^x - x - 1");
  const view = useMemo(
    () =>
      buildView(
        { text, x0Text, y0Text, endText, mode, amountText, exactText, method },
        strings
      ),
    [
      text,
      x0Text,
      y0Text,
      endText,
      mode,
      amountText,
      exactText,
      method,
      strings,
    ]
  );
  return (
    <>
      <div className="grid gap-4 sm:grid-cols-3">
        <div className="sm:col-span-3">
          <TextField
            hint={strings.functionHint}
            id="em-function"
            label={strings.functionLabel}
            mono
            onChange={setText}
            value={text}
          />
        </div>
        <TextField
          id="em-x0"
          label={strings.x0Label}
          mono
          onChange={setX0Text}
          value={x0Text}
        />
        <TextField
          id="em-y0"
          label={strings.y0Label}
          mono
          onChange={setY0Text}
          value={y0Text}
        />
        <TextField
          id="em-end"
          label={strings.endLabel}
          mono
          onChange={setEndText}
          value={endText}
        />
        <SelectField
          id="em-mode"
          label={strings.modeLabel}
          onChange={setMode}
          options={MODES.map((value) => ({
            value,
            label: strings.modes[value],
          }))}
          value={mode}
        />
        <TextField
          id="em-amount"
          label={mode === "step" ? strings.stepLabel : strings.countLabel}
          mono
          numeric
          onChange={setAmountText}
          value={amountText}
        />
        <SelectField
          id="em-method"
          label={strings.methodLabel}
          onChange={setMethod}
          options={METHODS.map((value) => ({
            value,
            label: strings.methods[value],
          }))}
          value={method}
        />
        <div className="sm:col-span-3">
          <TextField
            hint={strings.exactHint}
            id="em-exact"
            label={strings.exactLabel}
            mono
            onChange={setExactText}
            value={exactText}
          />
        </div>
      </div>
      <section aria-live="polite" className="space-y-5">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        {view.kind === "problem" ? (
          <ErrorNotice>{view.message}</ErrorNotice>
        ) : null}
        {view.kind === "ok" ? (
          <OdeResults fmt={fmt} strings={strings} view={view} />
        ) : null}
      </section>
    </>
  );
}
